import type { AssistantPageContext } from '@/api/assistant/assistantApi.types';
import { httpClient } from '@/api/httpClient';
import {
  VOICE_CALL_TIMEOUT_MILLISECONDS,
  VOICE_TOOL_CALL_TIMEOUT_MILLISECONDS,
} from '@/constants/assistantVoice';
import type {
  VoiceCall,
  VoiceCallApiResource,
  VoiceHistoryEntryApiResource,
  VoiceHistoryRole,
  VoiceToolCallApiResource,
  VoiceToolCallResult,
} from './assistantVoiceApi.types';

const VOICE_BASE_PATH = '/assistant/voice';

export async function startVoiceCall(
  offerSdp: string,
  pageContext: AssistantPageContext,
  openSessionNotePatientId: string | null
): Promise<VoiceCall> {
  const response = await httpClient.post<VoiceCallApiResource>(
    VOICE_BASE_PATH + '/calls',
    {
      offer_sdp: offerSdp,
      page_context: {
        route_key: pageContext.routeKey,
        entity_type: pageContext.entityType,
        entity_id: pageContext.entityId,
      },
      open_session_note_patient_id: openSessionNotePatientId,
    },
    { timeout: VOICE_CALL_TIMEOUT_MILLISECONDS }
  );
  return {
    answerSdp: response.data.answer_sdp,
    maxSessionSeconds: response.data.max_session_seconds,
    minimumAverageLogProbability: response.data.minimum_average_log_probability,
    recordingConfirmationInstructions: response.data.recording_confirmation_instructions,
  };
}

export async function runVoiceToolCall(
  name: string,
  argumentsJson: string
): Promise<VoiceToolCallResult> {
  const response = await httpClient.post<VoiceToolCallApiResource>(
    VOICE_BASE_PATH + '/tool-calls',
    { name, arguments: argumentsJson },
    { timeout: VOICE_TOOL_CALL_TIMEOUT_MILLISECONDS }
  );
  return { output: response.data.output, uiAction: response.data.ui_action };
}

export async function recordVoiceHistoryEntry(
  conversationId: string | null,
  role: VoiceHistoryRole,
  text: string
): Promise<string> {
  const response = await httpClient.post<VoiceHistoryEntryApiResource>(
    VOICE_BASE_PATH + '/history',
    { conversation_id: conversationId, role, text },
    { timeout: VOICE_TOOL_CALL_TIMEOUT_MILLISECONDS }
  );
  return response.data.conversation_id;
}
